import { ModalOpenButton } from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import {
  namedOperations,
  ProjectSourcePageName,
} from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import { CreateProjectModal } from "../modals/CreateProjectModal.js";

interface Props {
  onCreate?: (projectId: string) => Promise<void> | void;
  crmOrganisation?: React.ComponentProps<
    typeof CreateProjectModal
  >["crmOrganisation"];
  building?: React.ComponentProps<typeof CreateProjectModal>["building"];
  buildingItemId?: string;
  pageName: ProjectSourcePageName;
  refetchQueries?: string[];
  autoNavigate?: boolean;
  type: "icon" | "button";
  size?: "small" | "large";
  desiredProjectInitialStatus?: "opportunity" | "contracted";
}

export const CreateProjectButton = ({
  onCreate,
  crmOrganisation,
  building,
  buildingItemId,
  pageName,
  type,
  size = "small",
  refetchQueries,
  autoNavigate = true,
  desiredProjectInitialStatus,
}: Props) => {
  const { t } = useTranslate("ProjectCreate");
  const navigate = useNavigate();

  const viewer = useUserData().currentUser!;

  const title = t("New project");

  return (
    <ModalOpenButton
      Modal={CreateProjectModal}
      modalProps={{
        refetchQueries: [
          namedOperations.Query.ProjectsList,
          ...(refetchQueries ?? []),
        ],
        crmOrganisation,
        building,
        buildingItemId,
        pageName,
        handleComplete: async projectId => {
          await onCreate?.(projectId);
          if (autoNavigate) {
            navigate({
              pathname: `/projects/${projectId}/overview`,
              search: new URLSearchParams({ create: "true" }).toString(),
            });
          }
        },
        desiredProjectInitialStatus:
          desiredProjectInitialStatus ??
          (viewer.organisation.isCraftsmanOrganisation
            ? "contracted"
            : "opportunity"),
      }}
    >
      {type === "button" ? (
        <ButtonCreate title={title} sx={{ flexShrink: 0, flexGrow: 0 }} />
      ) : type === "icon" ? (
        <IconButton
          color="primary"
          aria-label={title}
          size={size}
          sx={{ flexShrink: 0, flexGrow: 0 }}
        >
          <Tooltip title={title}>
            <AddIcon />
          </Tooltip>
        </IconButton>
      ) : (
        <div />
      )}
    </ModalOpenButton>
  );
};
